import React, { useState, useEffect, useCallback } from 'react';
import { GameConfigService } from '../services/GameConfigService';
import { GameConfig } from '../types';
import './GameManagement.css';

type SortField = 'name' | 'plays' | 'status';
type SortDirection = 'asc' | 'desc';

export const GameManagement: React.FC = () => {
  const [games, setGames] = useState<GameConfig[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const configService = GameConfigService.getInstance();

  const loadGames = useCallback(() => {
    setGames(configService.getGameConfigs());
  }, [configService]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const handleToggle = (gameId: string, enabled: boolean) => {
    configService.setGameEnabled(gameId, enabled);
    loadGames();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredGames = games
    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'plays':
          cmp = a.totalPlays - b.totalPlays;
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="admin-games">
      <div className="admin-games__header">
        <h2 className="admin-games__title">Game Management</h2>
        <span className="admin-games__count">{games.length} games</span>
      </div>

      <div className="admin-games__controls">
        <input
          className="admin-games__search"
          type="text"
          placeholder="Search games..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          aria-label="Search games"
        />
      </div>

      <div className="admin-games__table-wrapper">
        <table className="admin-games__table">
          <thead>
            <tr>
              <th className="admin-games__th">Game</th>
              <th
                className="admin-games__th admin-games__th--sortable"
                onClick={() => handleSort('name')}
              >
                Name{sortIndicator('name')}
              </th>
              <th
                className="admin-games__th admin-games__th--sortable"
                onClick={() => handleSort('status')}
              >
                Status{sortIndicator('status')}
              </th>
              <th
                className="admin-games__th admin-games__th--sortable"
                onClick={() => handleSort('plays')}
              >
                Plays{sortIndicator('plays')}
              </th>
              <th className="admin-games__th">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {filteredGames.map(game => (
              <tr key={game.id} className={`admin-games__row ${!game.enabled ? 'admin-games__row--disabled' : ''}`}>
                <td className="admin-games__td">
                  <span className="admin-games__icon">{game.icon}</span>
                </td>
                <td className="admin-games__td">
                  <div className="admin-games__name">{game.name}</div>
                  <div className="admin-games__meta">{game.playerCount} · {game.difficulty}</div>
                </td>
                <td className="admin-games__td">
                  <span className={`admin-games__status admin-games__status--${game.status}`}>
                    {game.status === 'available' ? '● Active' : game.status === 'disabled' ? '● Disabled' : '● Coming Soon'}
                  </span>
                </td>
                <td className="admin-games__td admin-games__td--plays">
                  {game.totalPlays}
                </td>
                <td className="admin-games__td">
                  <label className="admin-games__toggle" aria-label={`Toggle ${game.name}`}>
                    <input
                      type="checkbox"
                      checked={game.enabled}
                      onChange={e => handleToggle(game.id, e.target.checked)}
                      className="admin-games__toggle-input"
                    />
                    <span className="admin-games__toggle-slider" />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredGames.length === 0 && (
        <p className="admin-games__empty">No games match your search.</p>
      )}
    </div>
  );
};
